import React, { useCallback, useEffect, useRef, useState } from 'react'
import { SketchPicker } from 'react-color'
import { useDispatch, useSelector } from 'react-redux'

import { Color } from 'constants/color'
import { GET_WHITEBOARD, BROADCAST_WHITEBOARD } from 'constants/socket'
import { isToday, getCurrentDate } from 'helpers/date'
import { throttle, debounce } from 'helpers/lodash'
import { notify } from 'helpers/notification'
import { getRoomName } from 'helpers/room'
import useVideoContext from 'hooks/useVideoContext/useVideoContext'
import { postWhiteboardData, getWhiteboardData } from 'services/apis/whiteboard'
import { receiveAllMessage } from 'store/actions/WebSocket'
import { setWhiteboardSize } from 'store/actions/Whiteboard'
import { dispatchSendSocketMessage } from 'store/dispatcher'
import { _isTeacher, _userId } from 'store/selectors/User'
import { _websocketMsg } from 'store/selectors/WebSocket'
import { _isWhiteboardFullSize } from 'store/selectors/Whiteboard'
import { isDesktop, isIPad } from 'utils/device'
import { logger } from 'utils/logger'

import {
    SUBSCRIBE_TO_ALL,
    MAX_PAGE_REACHED_MESSAGE,
    THROTTLE_PERIOD,
    DEBOUNCE_PERIOD,
    PAGE_LIMIT,
} from './Constants'
import { CanvasStyle, SketchPickerContainer, Sketch } from './Styles'

/*components imported*/
import ControlBarBottom from '../../ControlBar/ControlBarBottom/index'
import ControlBarLeft from '../../ControlBar/ControlBarLeft/index'

const roomName = getRoomName()

export default function Whiteboard({ remoteDataPub }) {
    const wbRef = useRef(null)
    const { dataTrack } = useVideoContext()
    const isTeacher = useSelector(_isTeacher)
    const msg = useSelector(_websocketMsg)
    const [color, setColor] = useState(Color.black)
    localStorage.setItem('color', color)
    const [isErase, setIsErase] = useState(false)
    localStorage.setItem('erase', isErase)
    const [isPlatterOpen, setPlatterOpen] = useState(false)
    const dispatch = useDispatch()
    const isWhiteboardFullSize = useSelector(_isWhiteboardFullSize)

    const localUserId = useSelector(_userId)

    const [currentPage, setCurrentPage] = useState(1)
    /*creating this ref to be used in unmount to save in localStorage*/
    const currentPageRef = useRef(1)
    const [totalPage, setTotalPage] = useState(1)
    /*creating this ref to be used in unmount to save in localStorage*/
    const totalPageRef = useRef(1)

    /* for receiving data from Teacher on Student side */
    const remoteDataTrack = useRef(remoteDataPub)
    remoteDataTrack.current = remoteDataPub

    /*stroke points and redo points*/
    let strokePointsArray = useRef([])

    const [redoPoints, setRedoPoints] = useState([])

    /*previous width and height of the canvas*/
    const prevWidth = useRef(null)
    const prevHeight = useRef(null)

    /*current width and height of the canvas*/
    const currentWidth = useRef(null)
    const currentHeight = useRef(null)

    /*set id we are getting from the backend*/
    const slideId = useRef(null)

    /*array to store scaled points and eventually
    set stroke points to it when there is size toggle*/
    //let scaledPoints = []
    const scaledPoints = useRef([])

    const whiteBoardDataKey = 'WB_DATA_' + roomName

    const whiteBoardDateKey = 'WB_DATE_' + roomName

    const disableLeftNavigation = currentPage === 1

    const disableRightNavigation = totalPage === currentPage

    const [disableUndo, setDisableUndo] = useState(true)

    const [disableRedo, setDisableRedo] = useState(true)

    const colorChangeHandler = () => {
        setPlatterOpen(!isPlatterOpen)
    }

    /*change size toggle*/
    const onSizeToggle = () => {
        /*only when student size is greater than 8 save data
        on size toggle*/
        dispatch(setWhiteboardSize(!isWhiteboardFullSize))
    }

    const dataTrackSend = useCallback(
        data => {
            try {
                dataTrack.send(data)
            } catch (error) {
                logger.error(error)
            }
        },
        [dataTrack]
    )

    // TODO: Fix Lint Issue Here
    // eslint-disable-next-line no-unused-vars
    const eraseHandler = (size, color) => {
        // TODO: Fix Lint Issue Here
        // eslint-disable-next-line no-unused-vars
        if (isPlatterOpen) {
            setPlatterOpen(!isPlatterOpen)
        }
        setIsErase(isErase => true)
    }

    const onPenClick = () => {
        // TODO: Fix Lint Issue Here
        // eslint-disable-next-line no-unused-vars
        if (isPlatterOpen) {
            setPlatterOpen(!isPlatterOpen)
        }
        setIsErase(isErase => false)
    }

    const handleChangeColorPlatter = color => {
        // TODO: Fix Lint Issue Here
        // eslint-disable-next-line no-unused-vars
        setIsErase(isErase => false)
        setColor(color.hex)
    }

    /*save data to backend*/
    const saveData = async () => {
        let data
        let height = currentHeight.current
        let width = currentWidth.current
        if (isTeacher) {
            /*also save height and width*/
            data = {
                strokePoints: strokePointsArray.current,
                height,
                width,
            }
            await postWhiteboardData(roomName, data, slideId.current)
        }
    }

    /* Common Canvas function */
    const clearCanvasHandler = () => {
        if (isTeacher) {
            const json = JSON.stringify({
                isClearPage: true,
            })
            dataTrackSend(json)
        }
        if (isPlatterOpen) {
            setPlatterOpen(!isPlatterOpen)
        }
        const canvas = document.getElementById('canvas')
        const context = canvas.getContext('2d')
        const memCanvas = document.getElementById('memCanvas')
        const memCtx = memCanvas.getContext('2d')
        context.clearRect(0, 0, canvas.width, canvas.height)
        memCtx.clearRect(0, 0, canvas.width, canvas.height)
        if (isTeacher) {
            strokePointsArray.current = []
            setRedoPoints([])
            setDisableRedo(true)
            setDisableUndo(true)
            saveData()
        }
    }

    /* Common Canvas function */
    const setStateOfCanvas = (strokePoints, height, width) => {
        //refresh logic
        let scale = true
        const canvas = document.getElementById('canvas')
        const context = canvas.getContext('2d')
        const memCanvas = document.getElementById('memCanvas')
        const memCtx = memCanvas.getContext('2d')
        /*clearing canvas and memcanvas */
        context.clearRect(0, 0, canvas.width, canvas.height)
        memCtx.clearRect(0, 0, canvas.width, canvas.height)
        strokePoints.forEach(strokePoints => {
            if (strokePoints.erase === 'true') {
                Erase(
                    context,
                    strokePoints.points,
                    strokePoints.pencolor,
                    true,
                    width,
                    height,
                    scale
                )
            } else {
                drawPoints(
                    context,
                    strokePoints.points,
                    strokePoints.pencolor,
                    false,
                    width,
                    height,
                    scale
                )
            }
        })
        memCtx.drawImage(canvas, 0, 0)
        /*set stroke points*/
        if (isTeacher) {
            strokePointsArray.current = scaledPoints.current
            strokePointsArray.current.length && setDisableUndo(false)
            scaledPoints.current = []
        }
    }

    const drawCanvasOnPageTurn = (strokePoints, height, width, pageTurn) => {
        let scale = true
        const canvas = document.getElementById('canvas')
        const context = canvas.getContext('2d')
        const memCanvas = document.getElementById('memCanvas')
        const memCtx = memCanvas.getContext('2d')
        let variableWidth = 0
        const interval = setInterval(() => {
            const startX = pageTurn === 'prev' ? 0 : canvas.width
            context.clearRect(startX, 0, variableWidth, canvas.height)
            memCtx.clearRect(startX, 0, variableWidth, canvas.height)
            variableWidth =
                pageTurn === 'prev' ? variableWidth + 300 : variableWidth - 300
            if (
                pageTurn === 'prev'
                    ? variableWidth > canvas.width
                    : variableWidth < -canvas.width
            ) {
                /*removing remaining text*/
                context.clearRect(0, 0, canvas.width, canvas.height)
                memCtx.clearRect(0, 0, canvas.width, canvas.height)
                strokePoints.forEach(strokePoints => {
                    if (strokePoints.erase === 'true') {
                        Erase(
                            context,
                            strokePoints.points,
                            strokePoints.pencolor,
                            true,
                            width,
                            height,
                            scale
                        )
                    } else {
                        drawPoints(
                            context,
                            strokePoints.points,
                            strokePoints.pencolor,
                            false,
                            width,
                            height,
                            scale
                        )
                    }
                })
                memCtx.drawImage(canvas, 0, 0)
                if (isTeacher) {
                    strokePointsArray.current = scaledPoints.current
                    strokePointsArray.current.length && setDisableUndo(false)
                    scaledPoints.current = []
                } else {
                    scaledPoints.current = []
                }
                clearInterval(interval)
            }
        }, 25)
    }

    const undo = () => {
        if (isPlatterOpen) {
            setPlatterOpen(!isPlatterOpen)
        }
        const canvas = document.getElementById('canvas')
        const context = canvas.getContext('2d')
        const memCanvas = document.getElementById('memCanvas')
        const memCtx = memCanvas.getContext('2d')
        if (strokePointsArray.current.length) {
            let fileterdStrokePointsForUndo = strokePointsArray.current.filter(
                (_, index) => index !== strokePointsArray.current.length - 1
            )
            context.clearRect(0, 0, canvas.width, canvas.height)
            memCtx.clearRect(0, 0, canvas.width, canvas.height)
            fileterdStrokePointsForUndo.forEach(stroke => {
                if (stroke.erase === 'true') {
                    Erase(
                        context,
                        stroke.points,
                        stroke.pencolor,
                        true,
                        currentWidth.current,
                        currentHeight.current
                    )
                } else {
                    drawPoints(
                        context,
                        stroke.points,
                        stroke.pencolor,
                        false,
                        currentWidth.current,
                        currentHeight.current
                    )
                }
            })
            memCtx.drawImage(canvas, 0, 0)
            const jsonMove = JSON.stringify({
                strokePoints: fileterdStrokePointsForUndo,
                width: currentWidth.current,
                height: currentHeight.current,
                undoRedo: true,
            })
            dataTrackSend(jsonMove)
            const redoPoint = strokePointsArray.current.filter(
                (_, index) => index === strokePointsArray.current.length - 1
            )
            setRedoPoints(redoPoints => [...redoPoints, redoPoint[0]])
            setDisableRedo(false)
            strokePointsArray.current = fileterdStrokePointsForUndo
            if (!strokePointsArray.current.length) {
                setDisableUndo(true)
            }
        }
    }

    const redo = () => {
        if (isPlatterOpen) {
            setPlatterOpen(!isPlatterOpen)
        }
        const canvas = document.getElementById('canvas')
        const context = canvas.getContext('2d')
        const memCanvas = document.getElementById('memCanvas')
        const memCtx = memCanvas.getContext('2d')
        if (redoPoints.length) {
            let pointsToRedo = redoPoints.filter(
                (_, index) => index === redoPoints.length - 1
            )
            let addedStrokePointsForRedo = [
                ...strokePointsArray.current,
                pointsToRedo[0],
            ]
            context.clearRect(0, 0, canvas.width, canvas.height)
            memCtx.clearRect(0, 0, canvas.width, canvas.height)
            addedStrokePointsForRedo.forEach(stroke => {
                if (stroke.erase === 'true') {
                    Erase(
                        context,
                        stroke.points,
                        stroke.pencolor,
                        true,
                        currentWidth.current,
                        currentHeight.current
                    )
                } else {
                    drawPoints(
                        context,
                        stroke.points,
                        stroke.pencolor,
                        false,
                        currentWidth.current,
                        currentHeight.current
                    )
                }
            })
            memCtx.drawImage(canvas, 0, 0)
            const jsonMove = JSON.stringify({
                strokePoints: addedStrokePointsForRedo,
                width: currentWidth.current,
                height: currentHeight.current,
                undoRedo: true,
            })
            dataTrackSend(jsonMove)
            if (redoPoints.length === 1) {
                setDisableRedo(true)
            }
            setRedoPoints(
                redoPoints.filter((_, index) => index !== redoPoints.length - 1)
            )
            strokePointsArray.current = addedStrokePointsForRedo
            setDisableUndo(false)
        }
    }

    console.count('Whiteboard')

    /* For setting height and width on mount and whenever whiteboard size changes*/
    useEffect(() => {
        const { sizeWidth, sizeHeight } = responsiveCanvasDimensions()
        //Setting the canvas site and width to be responsive
        wbRef.current.width = sizeWidth
        wbRef.current.height = sizeHeight
        wbRef.current.style.width = sizeWidth
        wbRef.current.style.height = sizeHeight
        /*setting the width and height of memCanvas*/
        if (document.getElementById('memCanvas')) {
            document.getElementById('memCanvas').width = sizeWidth
            document.getElementById('memCanvas').height = sizeHeight
        }
    }, [isWhiteboardFullSize, window.innerHeight, window.innerWidth])

    /*get the current and previous width and height on mount and whenever the state changes*/
    useEffect(() => {
        const { sizeWidth, sizeHeight } = responsiveCanvasDimensions()
        if (!currentWidth.current && !currentHeight.current) {
            prevWidth.current = sizeWidth
            prevHeight.current = sizeHeight
        } else {
            prevWidth.current = currentWidth.current
            prevHeight.current = currentHeight.current
        }
        currentWidth.current = sizeWidth
        currentHeight.current = sizeHeight
    }, [isWhiteboardFullSize, window.innerHeight, window.innerWidth])

    const firstRender = useRef(true)

    /*not to run this useEffect on first render,run it
    whenever the size changes*/
    useEffect(() => {
        if (firstRender.current) {
            firstRender.current = false
            return
        }
        let scale = true
        if (isTeacher) {
            const canvas = document.getElementById('canvas')
            const context = canvas.getContext('2d')
            const memCanvas = document.getElementById('memCanvas')
            const memCtx = memCanvas.getContext('2d')
            /*setting redo points to empty and disable redo*/
            setRedoPoints([])
            setDisableRedo(true)
            if (strokePointsArray.current.length) {
                context.clearRect(0, 0, canvas.width, canvas.height)
                memCtx.clearRect(0, 0, canvas.width, canvas.height)
                strokePointsArray.current.forEach(strokePoints => {
                    if (strokePoints.erase === 'true') {
                        Erase(
                            context,
                            strokePoints.points,
                            strokePoints.pencolor,
                            true,
                            prevWidth.current,
                            prevHeight.current,
                            scale
                        )
                    } else {
                        drawPoints(
                            context,
                            strokePoints.points,
                            strokePoints.pencolor,
                            false,
                            prevWidth.current,
                            prevHeight.current,
                            scale
                        )
                    }
                })
                memCtx.drawImage(canvas, 0, 0)
                strokePointsArray.current = scaledPoints.current
                scaledPoints.current = []
            }
        }
    }, [isWhiteboardFullSize, window.innerHeight, window.innerWidth])

    /* For creating in-memory Canvas */
    const createCanvas = canvas => {
        const memCanvas = document.createElement('canvas')
        memCanvas.id = 'memCanvas'
        memCanvas.style.display = 'none'
        memCanvas.width = canvas.width
        memCanvas.height = canvas.height

        return memCanvas
    }

    useEffect(() => {
        const canvas = document.getElementById('canvas')
        const context = canvas.getContext('2d')

        var started = false

        // create an in-memory canvas
        var memCanvas = createCanvas(canvas)
        var sketch = document.getElementById('sketch')
        sketch.appendChild(memCanvas)
        var memCtx = memCanvas.getContext('2d')
        var points = []

        function pointerDown(e) {
            var m = getMouse(e, canvas)
            points.push({
                x: m.x,
                y: m.y,
            })
            started = true
        }

        function pointerMove(e) {
            if (!started) return
            const erase = localStorage.getItem('erase')
            const pencolor = localStorage.getItem('color')

            var m = getMouse(e, canvas)
            points.push({
                x: m.x,
                y: m.y,
            })

            const width = currentWidth.current || canvas.width
            const height = currentHeight.current || canvas.height
            if (erase === 'false') {
                context.clearRect(0, 0, width, height)
                // put back the saved content
                context.drawImage(memCanvas, 0, 0)
                drawPoints(context, points, pencolor, erase, width, height)
            } else {
                memCtx.clearRect(0, 0, width, height)
                memCtx.drawImage(canvas, 0, 0)
                Erase(context, points, pencolor, erase, width, height)
            }

            const json = JSON.stringify({
                started,
                points,
                pencolor,
                erase,
                width,
                height,
            })
            dataTrackSend(json)
        }

        function pointerUp() {
            if (!started) return

            started = false
            // When the pen is done, save the resulting context
            // to the in-memory canvas
            const erase = localStorage.getItem('erase')
            const pencolor = localStorage.getItem('color')
            memCtx.clearRect(0, 0, canvas.width, canvas.height)
            memCtx.drawImage(canvas, 0, 0)
            /*saving stroke points for undo redo and scaling*/
            strokePointsArray.current.push({ pencolor, erase, points })
            setDisableUndo(false)
            const json = JSON.stringify({
                started,
                erase,
            })
            dataTrackSend(json)
            points = []
        }

        const removeCanvasEventListeners = () => {
            canvas.removeEventListener('pointermove', pointerMove, false)

            canvas.removeEventListener('pointerdown', pointerDown, false)

            canvas.removeEventListener('pointerup', pointerUp, false)

            canvas.removeEventListener('mouseout', pointerUp, false)
        }

        if (isTeacher) {
            if (window.PointerEvent) {
                canvas.addEventListener('pointermove', pointerMove, false)

                canvas.addEventListener('pointerdown', pointerDown, false)

                canvas.addEventListener('pointerup', pointerUp, false)

                /*adding mouseout event so that when the pointer is dragged out 
                it calls pointerup and saves the stroke points*/
                canvas.addEventListener('mouseout', pointerUp, false)
            }
        }
        return () => {
            /*on umounting save data to backend and local storage*/
            if (isTeacher) {
                saveData()
                localStorage.setItem(
                    whiteBoardDataKey,
                    JSON.stringify({
                        strokePoints: strokePointsArray.current,
                        height: currentHeight.current,
                        width: currentWidth.current,
                        //saving slideId, currentPage and totalPage
                        slideId: slideId.current,
                        currentPage: currentPageRef.current,
                        totalPage: totalPageRef.current,
                    })
                )
                localStorage.setItem(whiteBoardDateKey, getCurrentDate())
                removeCanvasEventListeners()
            }
        }
    }, [])

    /* Common Drawing functions */
    const responsiveCanvasDimensions = () => {
        const elem = document.getElementById('canvas')

        const newCoords = {
            sizeWidth: elem.offsetWidth,
            sizeHeight: elem.offsetHeight,
        }
        return newCoords
    }

    /*maintaing index in this function to delete previous indexes and fill with 
    scaled indexes*/
    function draw(ctx, points, scaleX, scaleY, col, Erase, scale) {
        let i
        let scaledArray = []
        // draw a basic circle instead
        if (points.length < 6) {
            var b = points[0]
            ctx.beginPath()
            ctx.arc(
                b.x * scaleX,
                b.y * scaleY,
                ctx.lineWidth / 2,
                0,
                Math.PI * 2,
                !0
            )
            ctx.closePath()
            ctx.fill()
            return
        }
        ctx.beginPath()
        ctx.moveTo(points[0].x * scaleX, points[0].y * scaleY)
        if (isTeacher && scale) {
            scaledArray.push({
                x: points[0].x * scaleX,
                y: points[0].y * scaleY,
            })
        }
        // draw a bunch of quadratics, using the average of two points as the control point
        for (i = 1; i < points.length - 2; i++) {
            var c = (points[i].x * scaleX + points[i + 1].x * scaleX) / 2,
                d = (points[i].y * scaleY + points[i + 1].y * scaleY) / 2
            ctx.quadraticCurveTo(
                points[i].x * scaleX,
                points[i].y * scaleY,
                c,
                d
            )
            if (isTeacher && scale) {
                scaledArray.push({
                    x: points[i].x * scaleX,
                    y: points[i].y * scaleY,
                })
            }
        }
        ctx.quadraticCurveTo(
            points[i].x * scaleX,
            points[i].y * scaleY,
            points[i + 1].x * scaleX,
            points[i + 1].y * scaleY
        )
        if (isTeacher && scale) {
            scaledArray.push({
                x: points[i].x * scaleX,
                y: points[i].y * scaleY,
            })
            scaledArray.push({
                x: points[i + 1].x * scaleX,
                y: points[i + 1].y * scaleY,
            })
        }
        if (isTeacher && scale) {
            if (Erase) {
                scaledPoints.current.push({
                    pencolor: col,
                    erase: 'true',
                    points: scaledArray,
                })
            } else {
                scaledPoints.current.push({
                    pencolor: col,
                    erase: 'false',
                    points: scaledArray,
                })
            }
        }
        ctx.stroke()
    }

    const drawPoints = useCallback(
        (ctx, points, col, Erase, width, height, scale, alpha) => {
            const { sizeWidth, sizeHeight } = responsiveCanvasDimensions()
            let scaleX = sizeWidth / width
            let scaleY = sizeHeight / height
            ctx.lineWidth = 1.5
            ctx.lineJoin = 'round'
            ctx.lineCap = 'round'
            ctx.strokeStyle = col
            ctx.globalCompositeOperation = 'source-over'
            ctx.globalAlpha = alpha ? alpha : 1

            draw(ctx, points, scaleX, scaleY, col, Erase, scale)
        },
        [isWhiteboardFullSize, window.innerHeight, window.innerWidth]
    )

    const Erase = useCallback(
        (ctx, points, col, Erase, width, height, scale) => {
            const { sizeWidth, sizeHeight } = responsiveCanvasDimensions()

            let scaleX = sizeWidth / width
            let scaleY = sizeHeight / height

            ctx.lineWidth = 20
            ctx.lineJoin = 'round'
            ctx.lineCap = 'round'
            ctx.globalCompositeOperation = 'destination-out'
            ctx.strokeStyle = 'rgba(0,0,0,1)'

            draw(ctx, points, scaleX, scaleY, col, Erase, scale)
        },
        [isWhiteboardFullSize, window.innerHeight, window.innerWidth]
    )

    // Creates an object with x and y defined,
    // set to the mouse position relative to the state's canvas
    // If you wanna be super-correct this can be tricky,
    // we have to worry about padding and borders
    // takes an event and a reference to the canvas
    function getMouse(e, canvas) {
        var element = canvas,
            offsetX = 0,
            offsetY = 0,
            mx,
            my

        // Compute the total offset. It's possible to cache this if you want
        if (element.offsetParent !== undefined) {
            do {
                offsetX += element.offsetLeft
                offsetY += element.offsetTop
            } while ((element = element.offsetParent))
        }

        mx = e.pageX - offsetX
        my = e.pageY - offsetY

        // We return a simple javascript object with x and y defined
        return { x: mx, y: my }
    }

    const studentWB = gotData => {
        const canvas = document.getElementById('canvas')
        const context = canvas.getContext('2d')
        const memCanvas = document.getElementById('memCanvas')
        var memCtx = memCanvas.getContext('2d')
        const { started, points, pencolor, erase, width, height } = gotData
        if (!started) return
        if (erase === 'false') {
            context.clearRect(0, 0, canvas.width, canvas.height)
            // put back the saved content
            context.drawImage(memCanvas, 0, 0)
            drawPoints(context, points, pencolor, erase, width, height)
        } else {
            memCtx.clearRect(0, 0, canvas.width, canvas.height)
            memCtx.drawImage(canvas, 0, 0)
            Erase(context, points, pencolor, erase, width, height)
        }
    }

    const showToastMsg = throttle(() => {
        notify.warn(MAX_PAGE_REACHED_MESSAGE)
    }, THROTTLE_PERIOD)

    const onAddPage = debounce(async () => {
        setCurrentPage(totalPage + 1)
        currentPageRef.current = totalPage + 1
        setTotalPage(totalPage + 1)
        totalPageRef.current = totalPage + 1
        setRedoPoints([])
        setDisableRedo(true)
        setDisableUndo(true)
        await saveData()
        dispatchSendSocketMessage({
            action: BROADCAST_WHITEBOARD,
            identity: SUBSCRIBE_TO_ALL,
            pageTurn: 'add',
            strokePoints: [],
            height: currentHeight.current,
            width: currentWidth.current,
            totalPage: totalPageRef.current,
            currentPage: currentPageRef.current,
        })
        /*clear stroke points*/
        strokePointsArray.current = []
        if (isPlatterOpen) {
            setPlatterOpen(!isPlatterOpen)
        }
        const canvas = document.getElementById('canvas')
        drawCanvasOnPageTurn([], canvas.height, canvas.width, 'add')
        //calling postWhiteboardData to get the slideId
        const response = await postWhiteboardData(roomName, strokePointsArray)
        slideId.current = response.data.id
        logger.debug(`added a new page`)
    }, DEBOUNCE_PERIOD)

    const onPrev = debounce(async () => {
        if (currentPage <= 1) return

        await saveData()
        const resp = await getWhiteboardData(roomName)
        const previousSlideData = resp.data.data.filter(
            data => data.slide_index === currentPage - 1
        )
        const {
            strokePoints,
            height,
            width,
        } = previousSlideData[0].slide_data.data
        slideId.current = previousSlideData[0].slide_data.id
        setCurrentPage(previousSlideData[0].slide_index)
        currentPageRef.current = previousSlideData[0].slide_index
        drawCanvasOnPageTurn(strokePoints, height, width, 'prev')
        setRedoPoints([])
        setDisableRedo(true)
        !strokePoints.length ? setDisableUndo(true) : setDisableUndo(false)
        /*broadcast to students*/
        dispatchSendSocketMessage({
            action: BROADCAST_WHITEBOARD,
            identity: SUBSCRIBE_TO_ALL,
            pageTurn: 'prev',
            strokePoints,
            height,
            width,
            totalPage: totalPageRef.current,
            currentPage: currentPageRef.current,
        })
        logger.debug(`navigated to the previous page`)
    }, DEBOUNCE_PERIOD)

    const onNext = debounce(async () => {
        if (currentPage >= totalPage) return

        await saveData()
        const resp = await getWhiteboardData(roomName)
        const nextSlideData = resp.data.data.filter(
            data => data.slide_index === currentPage + 1
        )
        const { strokePoints, height, width } = nextSlideData[0].slide_data.data
        slideId.current = nextSlideData[0].slide_data.id
        setCurrentPage(nextSlideData[0].slide_index)
        currentPageRef.current = nextSlideData[0].slide_index
        drawCanvasOnPageTurn(strokePoints, height, width, 'next')
        setRedoPoints([])
        setDisableRedo(true)
        !strokePoints.length ? setDisableUndo(true) : setDisableUndo(false)
        /*broadcast to students*/
        dispatchSendSocketMessage({
            action: BROADCAST_WHITEBOARD,
            identity: SUBSCRIBE_TO_ALL,
            pageTurn: 'next',
            strokePoints,
            height,
            width,
            totalPage: totalPageRef.current,
            currentPage: currentPageRef.current,
        })
        logger.debug(`navigated to the next page`)
    }, DEBOUNCE_PERIOD)

    const studentUndoRedo = gotData => {
        const { strokePoints, width, height } = gotData
        const canvas = document.getElementById('canvas')
        const context = canvas.getContext('2d')
        const memCanvas = document.getElementById('memCanvas')
        const memCtx = memCanvas.getContext('2d')
        context.clearRect(0, 0, canvas.width, canvas.height)
        memCtx.clearRect(0, 0, canvas.width, canvas.height)
        strokePoints.forEach(stroke => {
            if (stroke.erase === 'true') {
                Erase(
                    context,
                    stroke.points,
                    stroke.pencolor,
                    true,
                    width,
                    height
                )
            } else {
                drawPoints(
                    context,
                    stroke.points,
                    stroke.pencolor,
                    false,
                    width,
                    height
                )
            }
        })
        memCtx.drawImage(canvas, 0, 0)
    }

    /* student live data handler */
    useEffect(() => {
        if (isTeacher) return
        const canvas = document.getElementById('canvas')
        const memCanvas = document.getElementById('memCanvas')
        var memCtx = memCanvas.getContext('2d')

        remoteDataTrack.current &&
            remoteDataTrack.current.on('message', data => {
                const gotData = JSON.parse(data)
                if (gotData.undoRedo) {
                    studentUndoRedo(gotData)
                } else if (gotData.isClearPage) {
                    clearCanvasHandler()
                } else if (!gotData.started) {
                    memCtx.clearRect(0, 0, canvas.width, canvas.height)
                    memCtx.drawImage(canvas, 0, 0)
                } else {
                    studentWB(gotData)
                }
            })
    }, [remoteDataTrack.current])

    /* For getting live canvas data state via our WebSockets 
       on mount */
    useEffect(() => {
        /*only student can ask for data not vice versa*/
        if (!isTeacher) {
            dispatchSendSocketMessage({
                action: GET_WHITEBOARD,
                identity: localUserId,
            })
        }
    }, [isWhiteboardFullSize, window.innerHeight, window.innerWidth])

    /* For handling State Management via our WebSockets */
    useEffect(() => {
        if (!msg) return
        switch (msg.action) {
            case GET_WHITEBOARD:
                /*one way data communication with teacher sending data*/
                if (isTeacher) {
                    const height = currentHeight.current
                    const width = currentWidth.current
                    dispatchSendSocketMessage({
                        action: BROADCAST_WHITEBOARD,
                        identity: msg.identity,
                        strokePoints: strokePointsArray.current,
                        height,
                        width,
                        totalPage: totalPageRef.current,
                        currentPage: currentPageRef.current,
                    })
                }
                break
            case BROADCAST_WHITEBOARD:
                if (!isTeacher) {
                    /*broadcast only to matching identity*/
                    if (
                        msg.identity === localUserId ||
                        msg.identity === SUBSCRIBE_TO_ALL
                    ) {
                        if (['prev', 'next', 'add'].includes(msg.pageTurn)) {
                            drawCanvasOnPageTurn(
                                msg.strokePoints,
                                msg.height,
                                msg.width,
                                msg.pageTurn
                            )
                        } else {
                            setStateOfCanvas(
                                msg.strokePoints,
                                msg.height,
                                msg.width
                            )
                        }
                        setTotalPage(msg.totalPage)
                        setCurrentPage(msg.currentPage)
                    }
                }
                break
            default:
                break
        }
        return () => dispatch(receiveAllMessage(null))
    }, [msg])

    const getWbBackendData = () => {
        getWhiteboardData(roomName).then(response => {
            if (response.data.data.length) {
                setTotalPage(response.data.data.length)
                totalPageRef.current = response.data.data.length
                const activeSlideData = response.data.data.filter(
                    data => data.slide_data.active_slide
                )
                const {
                    strokePoints,
                    height,
                    width,
                } = activeSlideData[0].slide_data.data
                slideId.current = activeSlideData[0].slide_data.id
                setCurrentPage(activeSlideData[0].slide_index)
                currentPageRef.current = activeSlideData[0].slide_index
                setStateOfCanvas(strokePoints, height, width)

                /*broadcast to students*/
                dispatchSendSocketMessage({
                    action: BROADCAST_WHITEBOARD,
                    identity: SUBSCRIBE_TO_ALL,
                    strokePoints,
                    height,
                    width,
                })
            } else {
                postWhiteboardData(roomName, strokePointsArray).then(
                    response => {
                        slideId.current = response.data.id
                    }
                )
            }
        })
    }

    useEffect(() => {
        if (!isTeacher) return
        /*check if wb data exists*/
        if (localStorage.getItem(whiteBoardDataKey)) {
            const dateOfData = new Date(localStorage.getItem(whiteBoardDateKey))
            /*check if todays date*/
            if (isToday(dateOfData)) {
                const wb_data = JSON.parse(
                    localStorage.getItem(whiteBoardDataKey)
                )
                setStateOfCanvas(
                    wb_data.strokePoints,
                    wb_data.height,
                    wb_data.width
                )
                /*broadcast to students*/
                dispatchSendSocketMessage({
                    action: BROADCAST_WHITEBOARD,
                    identity: SUBSCRIBE_TO_ALL,
                    strokePoints: wb_data.strokePoints,
                    height: wb_data.height,
                    width: wb_data.width,
                })

                setCurrentPage(wb_data.currentPage)
                currentPageRef.current = wb_data.currentPage
                setTotalPage(wb_data.totalPage)
                totalPageRef.current = wb_data.totalPage

                slideId.current = wb_data.slideId
                logger.info(`Whiteboard data present in local storage`)
            } else {
                localStorage.removeItem(whiteBoardDataKey)
                localStorage.removeItem(whiteBoardDateKey)
                getWbBackendData()
                logger.info(
                    `Previous day whiteboard data present in local storage`
                )
            }
        } else {
            getWbBackendData()
            logger.info(`Getting Whiteboard data from backend`)
        }
    }, [])

    return (
        <>
            {isTeacher ? (
                <>
                    <ControlBarLeft
                        onPenClick={onPenClick}
                        onChangeColor={colorChangeHandler}
                        onErase={eraseHandler}
                        clearCanvas={clearCanvasHandler}
                        isPlatterOpen={isPlatterOpen}
                        onSizeToggle={onSizeToggle}
                        disableRedo={disableRedo}
                        disableUndo={disableUndo}
                        onUndo={undo}
                        onRedo={redo}
                    />
                    {!isIPad && (
                        <ControlBarBottom
                            onAddPage={
                                totalPage < PAGE_LIMIT
                                    ? onAddPage
                                    : showToastMsg
                            }
                            onPrev={onPrev}
                            onNext={onNext}
                            currentPage={currentPage}
                            totalPage={totalPage}
                            disableAddPage={totalPage === PAGE_LIMIT}
                            disableLeftNavigation={disableLeftNavigation}
                            disableRightNavigation={disableRightNavigation}
                        />
                    )}
                </>
            ) : (
                isDesktop && (
                    <>
                        <ControlBarLeft onSizeToggle={onSizeToggle} />
                        <ControlBarBottom
                            currentPage={currentPage}
                            totalPage={totalPage}
                        />
                    </>
                )
            )}
            {isTeacher ? (
                <Sketch id="sketch">
                    <CanvasStyle
                        ref={wbRef}
                        id="canvas"
                        isTeacher={isTeacher}
                    ></CanvasStyle>
                </Sketch>
            ) : (
                <Sketch id="sketch">
                    <CanvasStyle ref={wbRef} id="canvas"></CanvasStyle>
                </Sketch>
            )}

            {!!isPlatterOpen && (
                <SketchPickerContainer>
                    <SketchPicker
                        color={color}
                        onChangeComplete={handleChangeColorPlatter}
                    />
                </SketchPickerContainer>
            )}
        </>
    )
}
